import axios from "axios"; // tslint:disable-line match-default-egGateway(xport-nam)e

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";

import { api } from "@courses-models/institution/src/ts/config";

/**
 * Describes data returned by v1/unic/Institution/{institutionNumber} from
 * Gyldendal Access Service.
 */
type v1_unic_Institution_Response = {
  Address: string;
  City: string;
  MunicipalityName: string;
  MunicipalityNumber: string;
  Name: string;
  ParentInstitution: string;
  PostCode: string;
  TypeCode: string;
  TypeName: string;
  INST_TYPE_3: string | number;
};

/**
 * Memory cache of users that have already been fetched by the client, so that
 * we don't need to fetch the same users over and over again.
 */
const cache = new EntityStore<InstitutionModel>({ node: false, ttl: 3600 });

/**
 * Specification of the data about a single institution in Uni-C.
 */
export class InstitutionModel {
  /**
   * Specifies the id of the institution.
   */
  public readonly id: string;

  /**
   * Specifies the institution's name.
   */
  public readonly name: string;

  /**
   * Specifies the address of the institution.
   */
  public readonly address: string;

  /**
   * Specifies the city that the institution resides in.
   */
  public readonly city: string;

  /**
   * Specifies the postal code of the area that the institution resides in.
   */
  public readonly postalCode: string;

  /**
   * Specifies the name of the municipality that the institution resides in.
   */
  public readonly municipality: string;

  /**
   * Specifies if this institution is known to be educating teachers (in that
   * case students of the institution should also be considered teachers, as
   * that is what they are studying to become).
   */
  public readonly isTeacherEducation: boolean;

  /**
   * Apply data initially when creating a user model.
   */
  constructor(data: {
    id: string;
    name: string;
    address: string;
    city: string;
    postalCode: string;
    municipality: string;
    isTeacherEducation: boolean;
  }) {
    this.id = data.id;
    this.name = data.name;
    this.address = data.address;
    this.city = data.city;
    this.postalCode = data.postalCode;
    this.municipality = data.municipality;
    this.isTeacherEducation = data.isTeacherEducation;

    // Cache the entity for future use
    cache.set(this.id, this);
  }

  /**
   * Helper that'll fetch an institution based on the given id.
   */
  @nonConcurrent()
  public static async GET_BY_ID(id: string): Promise<InstitutionModel> {
    // If this institution already exists in the cache, then return it
    // directly from there
    if (cache.has(id)) {
      return <InstitutionModel>cache.get(id);
    }

    // Fetch institution data from the remote server
    const endpoint = `${api.gyldendalAccessService(
      "v1"
    )}/api/v1/unic/Institution/${encodeURIComponent(id)}`;
    const response = await axios.get<v1_unic_Institution_Response>(gGateway(endpoint));

    // Create a new instance of the institution model
    return new InstitutionModel({
      id,
      name: response.data.Name,
      address: response.data.Address,
      city: response.data.City,
      postalCode: response.data.PostCode,
      municipality: response.data.MunicipalityName,
      isTeacherEducation:
        response.data.INST_TYPE_3 === 1085 ||
        response.data.INST_TYPE_3 === "1085",
    });
  }
}
